import Swal, { type SweetAlertOptions, type SweetAlertResult } from 'sweetalert2'

/**
 * Port of `Login.getCenteredSwalConfig`, which the Account pages wrapped every
 * dialog in: width 600px, 2rem padding, no dismiss on outside click.
 *
 * Its `didOpen` hook also forced `position: fixed` and flex centering onto the
 * container by hand. SweetAlert2 v11 centers by default, so that workaround is
 * dropped — it was fighting a v9/v10 layout quirk and the inline styles it wrote
 * are exactly what the library now sets itself.
 */
export function centeredSwal(options: SweetAlertOptions): Promise<SweetAlertResult> {
  return Swal.fire({
    position: 'center',
    backdrop: true,
    allowOutsideClick: false,
    width: '600px',
    padding: '2rem',
    ...options,
  })
}

/** The blocking "Please Wait" spinner login.js showed while sending an OTP. */
export function showLoading(message: string, title = 'Please Wait'): void {
  void Swal.fire({
    position: 'center',
    backdrop: true,
    allowOutsideClick: false,
    width: '600px',
    padding: '2rem',
    title,
    text: message,
    showConfirmButton: false,
    didOpen: () => Swal.showLoading(),
  })
}

export function closeAlert(): void {
  Swal.close()
}

/**
 * SweetAlert2 helpers.
 *
 * The Razor pages loaded SweetAlert from a CDN (_LoginLayout) or from
 * wwwroot/lib (_Layout) and called `Swal.fire` inline all over
 * wwwroot/js/app/**. It is an npm dependency here — same library, same look,
 * one fewer third-party origin the pages depend on at runtime.
 */
export function showSuccess(message: string, title = 'Success'): Promise<unknown> {
  return Swal.fire({ icon: 'success', title, text: message, confirmButtonText: 'OK' })
}

export function showError(message: string, title = 'Error'): Promise<unknown> {
  return Swal.fire({ icon: 'error', title, text: message, confirmButtonText: 'OK' })
}

export function showWarning(message: string, title = 'Warning'): Promise<unknown> {
  return Swal.fire({ icon: 'warning', title, text: message, confirmButtonText: 'OK' })
}

/** `Common.Alert.Info` */
export function showInfo(message: string, title = ''): Promise<unknown> {
  return Swal.fire({ icon: 'info', title, text: message, allowOutsideClick: true })
}

/** Returns true when the user confirms. Mirrors the confirm dialogs in the old JS. */
export async function confirmAction(
  message: string,
  title = 'Are you sure?',
  confirmButtonText = 'Yes',
): Promise<boolean> {
  const result = await Swal.fire({
    icon: 'warning',
    title,
    text: message,
    showCancelButton: true,
    confirmButtonText,
    cancelButtonText: 'Cancel',
  })
  return result.isConfirmed
}
